import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  forwardRef,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  Type,
  EventEmitter,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  AgCheckbox,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  ColDefUtil,
  ColumnApi,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { isExists } from '../../journal/utils';
import { TableColumn } from '../models/table-column.interface';
import { AgGridAngular, AgRendererComponent } from 'ag-grid-angular';
import { ColumnParams } from '../../journal';
import { TableColumnComponentNames, TableColumnType } from '../constants/table-column-type';
import { TableCellActionsPanelComponent } from '../renderers/table-cell-actions-panel/table-cell-actions-panel.component';
import { TableStringRenderComponent } from '../renderers/table-string-render/table-string-render.component';
import { TableCellCheckboxRendererComponent } from '../renderers/table-cell-checkbox-renderer/table-cell-checkbox-renderer.component';
import { TableCellStatusRendererComponent } from '../renderers/table-cell-status-renderer/table-cell-status-renderer.component';
import { TableCellEnumRendererComponent } from '../renderers/table-cell-enum-renderer/table-cell-enum-renderer.component';

export interface CellEditedParams<T> {
  rowIndex: number;
  record: T;
  value: any;
  field: any;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TableComponent), multi: true }],
})
export class TableComponent<T, F = void> implements OnInit, ControlValueAccessor, OnChanges, OnDestroy {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  @Input() overlayNoRowsTemplate = 'Отсутствуют данные для отображения';

  @Input() gridThemeName = 'ag-theme-alpine';

  @Input() columns: Array<TableColumn<T, any, any>> = [];

  @Input() rows: T[] = [];

  @Input() isEditable = false;

  @Input() selectable = false;

  @Input() multiSelect = false;

  @Input() fastFilter?: string;

  @Input() getId?: (data: T) => string;

  @Output() select: EventEmitter<T[]> = new EventEmitter();

  @Output() cellEdit: EventEmitter<CellEditedParams<T>> = new EventEmitter();

  getRowId: GetRowIdFunc = (params: GetRowIdParams) => params.data.id;
  // NOTE:
  colDefinitions: ColDef[] = [];

  selectionMode!: 'single' | 'multiple'; // = 'single';

  private gridApi!: GridApi;
  private columnApi!: ColumnApi;

  // NOTE: дополнительные подключаемые компоненты для отображения колонок на основании зарегистрированного текстового имени
  components: {
    [p: string]: any;
  } = {
    [TableColumnComponentNames.StringRender]: TableStringRenderComponent,
    [TableColumnComponentNames.ActionsPanel]: TableCellActionsPanelComponent,
    [TableColumnComponentNames.CheckboxRender]: TableCellCheckboxRendererComponent,
    [TableColumnComponentNames.CheckboxCellEditor]: AgCheckbox,
    [TableColumnComponentNames.StatusRender]: TableCellStatusRendererComponent,
    [TableColumnComponentNames.EnumRender]: TableCellEnumRendererComponent,
  };

  constructor(private zone: NgZone) {}

  ngOnInit() {}

  ngOnChanges({ columns, rows, multiSelect, fastFilter }: SimpleChanges): void {
    const hasApi = isExists(this.gridApi);
    const isColumnsChanged = isExists(columns);
    if (columns) {
      this.colDefinitions = this.getColDefinitions(this.columns, this.isEditable, this.selectable);
      if (!hasApi) {
      } else {
        // TODO: clear
        // const colDefs = (this.columnApi.getColumns() ?? []).map((col) => col.getColDef());
        this.gridApi.refreshHeader();
      }
    }
    if (isExists(multiSelect)) {
      this.selectionMode = this.multiSelect ? 'multiple' : 'single';
    }

    const isRowsChanged = isExists(rows);
    if (isRowsChanged || isColumnsChanged) {
      this.updateRowsToApi();
    }

    if (hasApi && fastFilter) {
      this.gridApi.setQuickFilter(this.fastFilter || '');
    }
  }

  ngOnDestroy(): void {}

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    this.columnApi = event.columnApi;

    this.updateRowsToApi();
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  writeValue(obj: T[]): void {
    this.rows = obj;
    this.updateRowsToApi();
  }

  onSelectionChanged() {
    const currentSelected = this.gridApi.getSelectedRows();
    this.select.emit(currentSelected);
  }

  onCellValueChanged(event: CellValueChangedEvent) {
    const editEvent: CellEditedParams<T> = {
      value: event.newValue,
      rowIndex: event.rowIndex!,
      record: event.data,
      field: event.colDef.field,
    };
    this.cellEdit.next(editEvent);
  }

  private updateRowsToApi() {
    const rows = this.canPassRowsToApi() ? this.rows : [];
    this.gridApi?.setRowData(rows);
  }

  private canPassRowsToApi() {
    return this.columns?.length > 0;
  }

  private onChange: (value: T[]) => void = () => void 0;

  // формирование всех колонок согласно полученным параметрам описания
  private getColDefinitions(columns: Array<TableColumn<T, any, any>>, editable: boolean, selectable: boolean = false) {
    return (columns || []).map((col, index) => {
      return this.getColDef(col as TableColumn<T, any, any>, editable, selectable, index);
    });
  }

  // получить описание одной колонки для отображения в таблице
  private getColDef<P>(
    column: TableColumn<P, any, any>,
    editable: boolean,
    selectable: boolean,
    index: number,
  ): ColDef {
    const cellRenderer = column.type.renderer === '' ? null : column.type.renderer;
    // @see https://www.ag-grid.com/angular-data-grid/column-properties/
    const colDef = {
      field: column.field as string,
      colId: column.id,
      // @see https://www.ag-grid.com/angular-data-grid/column-properties/#reference-header
      headerName: column.header.getDragLabel(),
      checkboxSelection: selectable && this.multiSelect && index === 0,
      headerCheckboxSelection: selectable && this.multiSelect && index === 0,
      cellRenderer, //: column.type.renderer || undefined,
      cellRendererParams: {
        placeholder: '–',
        ...column.renderParams,
        // renderer: column.type.component,
      },
      cellEditor: column.type.editor ?? null,
      // @see https://www.ag-grid.com/javascript-data-grid/column-properties/#reference-width-suppressSizeToFit
      suppressSizeToFit: column.fixedWidth,
      // @see https://www.ag-grid.com/angular-data-grid/column-properties/#reference-editing
      editable: this.getCellEditableCheck(editable, column),
      // @see https://www.ag-grid.com/javascript-data-grid/column-pinning/
      pinned: column.pinned,
      // @see https://www.ag-grid.com/angular-data-grid/column-properties/#reference-sort
      sortable: column.sortable,
      sortIndex: column.sortOrder ?? -1,
      valueGetter: undefined,
      minWidth: column.minWidth,
      width: column.width,
      resizable: column.resizable,
      flex: column.flex,
    } as ColDef;

    return colDef;
  }

  private getCellEditableCheck(
    editable: boolean,
    column: TableColumn<any, any, any>,
  ): boolean | (({ node: { rowIndex } }: { node: { rowIndex: number } }) => boolean) {
    if (!editable || !column.editable) {
      return false;
    }
    return true;
  }
}
